import { useState } from "react";
import { useParams } from "react-router-dom";
import { AlertCircle, ChevronRight, Users } from "lucide-react";
import { Badge } from "~/components/ui/badge";
import { Skeleton } from "~/components/ui/skeleton";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import CeConfigResponse, { CeConfig } from "~/types/interfaces/ce-config";
import { DomainDetail } from "./cohort-explorer/DomainDetail";
import { OlderVersionNotice } from "./cohort-explorer/OlderVersionNotice";
import { VersionHistoryMenu } from "./cohort-explorer/VersionHistoryMenu";
import { orderVersions } from "./cohort-explorer/config-view";

const CONFIGS_BASE = "/api/cohort-explorer/configurator/configs";

interface DatasetCohortExplorerProps {
  // Overrides the route param — set when rendered outside the DatasetDetail
  // route tree (the project session context sidebar), which has no :datasetId.
  datasetId?: string;
  // True when rendered inside the session context sidebar, which supplies its
  // own header, so the redundant page title is suppressed.
  sessionSidebarView?: boolean;
}

export const DatasetCohortExplorer = ({
  datasetId: datasetIdProp,
  sessionSidebarView = false,
}: DatasetCohortExplorerProps = {}) => {
  const { datasetId: routeDatasetId } = useParams();
  const datasetId = datasetIdProp ?? routeDatasetId;
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(null);

  const { data: versionList } = useFetcher<CeConfig[]>(
    datasetId ? `${CONFIGS_BASE}?dataset_id=${datasetId}` : null
  );
  const {
    data: activeConfig,
    error: activeError,
    isLoading: activeLoading,
  } = useFetcher<CeConfigResponse>(
    datasetId ? `${CONFIGS_BASE}/active?dataset_id=${datasetId}` : null,
    { shouldRetryOnError: false, revalidateOnFocus: false }
  );
  const versions = orderVersions(versionList ?? []);
  const latestVersion = versions.find((version) => version.isLatest);
  const latestNumber = latestVersion?.number ?? null;
  // The version list refetches on focus but the active config does not, so the
  // list decides which version is latest.
  const latestVersionId = latestVersion?.version_id ?? activeConfig?.version_id ?? null;
  const currentVersionId = selectedVersionId ?? latestVersionId;
  const currentVersionNumber =
    versions.find((version) => version.version_id === currentVersionId)?.number ?? null;
  const isViewingOlder =
    currentVersionId != null && latestVersionId != null && currentVersionId !== latestVersionId;

  const fetchVersionId =
    currentVersionId != null && currentVersionId !== activeConfig?.version_id
      ? currentVersionId
      : null;
  const { data: versionConfig, isLoading: versionLoading } = useFetcher<CeConfigResponse>(
    datasetId && fetchVersionId != null
      ? `${CONFIGS_BASE}/${fetchVersionId}?dataset_id=${datasetId}`
      : null,
    { shouldRetryOnError: false, revalidateOnFocus: false }
  );

  const config = fetchVersionId != null ? versionConfig : activeConfig;
  const configLoading = fetchVersionId != null ? versionLoading : activeLoading;
  const domains = config?.content?.tables ?? [];
  const subjectLabel = config?.content?.subject_label || "Participants";
  const selectedDomain = domains.find((domain) => domain.id === selectedDomainId) ?? null;

  const handleSelectVersion = (versionId: number | null) => {
    setSelectedVersionId(versionId);
    setSelectedDomainId(null);
  };

  const handleViewLatest = () => {
    setSelectedVersionId(null);
    setSelectedDomainId(null);
  };

  if (activeLoading) {
    return <CohortExplorerLoading />;
  }

  // The active-config endpoint returns `{}` (200 OK), not an error, when the
  // dataset has no published configuration yet, so that case falls through to
  // the empty-tables check below. A 404 is treated as the same empty state
  // defensively; any other error is a real failure we must surface.
  const notConfigured = activeError?.status === 404;
  if (activeError && !notConfigured) {
    return <CohortExplorerError />;
  }
  if (notConfigured || !activeConfig?.content?.tables?.length) {
    return <CohortExplorerEmpty sessionSidebarView={sessionSidebarView} />;
  }

  if (selectedDomain) {
    return (
      <div className="mx-auto max-w-4xl">
        <DomainDetail
          domain={selectedDomain}
          versionNumber={currentVersionNumber}
          isViewingOlder={isViewingOlder}
          onBack={() => setSelectedDomainId(null)}
          onViewLatest={handleViewLatest}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-8">
      <div className="flex flex-col gap-4">
        {!sessionSidebarView && <TabHeader title="Cohort Explorer" />}
        <VersionHistoryMenu
          versions={versions}
          currentVersionId={currentVersionId}
          onSelectVersion={handleSelectVersion}
        />
      </div>

      {isViewingOlder && (
        <OlderVersionNotice onViewLatest={handleViewLatest}>
          You&apos;re viewing an older version.
          {latestNumber != null && ` The latest published version is Version ${latestNumber}.`}
        </OlderVersionNotice>
      )}

      <section className="flex flex-col gap-4">
        <div className="border-b border-border pb-3">
          <h3 className="text-lg font-semibold">Tables</h3>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            These tables are configured for data exploration and cohort creation in this dataset.
            Select one to view its configured columns.
          </p>
        </div>

        {configLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-14 rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-border">
            {domains.map((domain) => (
              <button
                key={domain.id ?? domain.name}
                type="button"
                onClick={() => setSelectedDomainId(domain.id ?? null)}
                className="flex w-full cursor-pointer items-center gap-3 border-b border-border px-4 py-4 text-left transition-colors last:border-b-0 hover:bg-muted/50"
              >
                <span
                  className={cn(
                    "size-3 shrink-0 rounded-full",
                    !domain.icon && "bg-muted-foreground"
                  )}
                  style={domain.icon ? { backgroundColor: domain.icon } : undefined}
                  aria-hidden="true"
                />
                <span className="font-medium text-foreground">{domain.name}</span>
                {domain.primary && <Badge variant="secondary">Primary</Badge>}
                <span className="ml-auto text-sm text-muted-foreground">
                  {(domain.columns ?? []).length} columns
                </span>
                <ChevronRight className="size-4 text-muted-foreground" aria-hidden="true" />
              </button>
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="text-lg font-semibold">Subject Type</h3>
        <p className="mt-1 mb-3 max-w-xl text-sm text-muted-foreground">
          The type of study subject used to label and organize the data.
        </p>
        <Badge variant="secondary">
          <Users aria-hidden="true" />
          {subjectLabel}
        </Badge>
      </section>
    </div>
  );
};

const CohortExplorerLoading = () => {
  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-9 w-80 rounded-full" />
      <div className="flex flex-col gap-2">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-14 rounded-lg" />
        ))}
      </div>
    </div>
  );
};

const CohortExplorerEmpty = ({ sessionSidebarView = false }: { sessionSidebarView?: boolean }) => {
  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-8">
      {!sessionSidebarView && <TabHeader title="Cohort Explorer" />}
      <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
        <span className="flex size-14 items-center justify-center rounded-full bg-muted">
          <Users className="size-6 text-muted-foreground" aria-hidden="true" />
        </span>
        <div className="text-lg font-semibold">No active configuration</div>
        <p className="max-w-md text-sm text-muted-foreground">
          This dataset doesn&apos;t have a published Cohort Explorer configuration yet. Once tables
          are configured and published, they&apos;ll appear here for exploration.
        </p>
      </div>
    </div>
  );
};

const CohortExplorerError = () => {
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-3 py-16 text-center text-destructive"
    >
      <AlertCircle className="size-8" aria-hidden="true" />
      <p className="text-sm font-medium">Failed to load Cohort Explorer configuration</p>
    </div>
  );
};
